import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAudioStore } from '@/stores/audioStore';
import { AudioTitleMeta } from '@/components/audio/shared/AudioTitleMeta';
import { AudioProgressBar } from '@/components/audio/shared/AudioProgressBar';
import { AudioBigControls } from '@/components/audio/shared/AudioBigControls';
import { AudioMiniControls } from '@/components/audio/shared/AudioMiniControls';
import { WORLD_NAME_BY_SLUG } from '@/components/audio/shared/helpers';
import { ShareBottomSheet } from '@/features/story/share/ShareBottomSheet';
import { resolveStoryMode } from '@/features/story/share/story-mode';
import { fetchStoryById } from '@/features/story/api';
import { ApiError, NetworkError } from '@/lib/api-client';
import { Toast } from '@/components/ui/toast';
import { STRINGS } from '@/strings/strings';
import type { StoryDetail } from '@/features/story/types';
import { NowPlayingTopNav } from './NowPlayingTopNav';
import { NowPlayingCover } from './NowPlayingCover';
import { NowPlayingMenu } from './NowPlayingMenu';
import { SchlafTimerSheet } from './SchlafTimerSheet';

const ERROR_TOAST_DURATION_MS = 2000;

/**
 * Full-screen Now-Playing page. Variante 02 gradient shell (world-aa top
 * → dream-navy bottom, matching the Inline-Player orientation).
 *
 * Layout:
 *   - Mobile / Tablet: vertical stack. TopNav → Cover → TitleMeta →
 *     ProgressBar → BigControls → MiniControls.
 *   - Desktop (≥1440 px): horizontal. TopNav spans full width at the top;
 *     below, Cover (320 × 320) sits left, the Title/Progress/Controls
 *     column fills the rest.
 *
 * Runs INSIDE AppShell so the singleton `<audio>` element, the audio-
 * store's `_audio` ref, and the `useMediaSession` hook all survive the
 * route change. AppShell conditionally hides its own chrome (TopBar,
 * BottomNav, MiniPlayer, EmailVerificationBanner, WorldSelectionSheet)
 * when `location.pathname === '/now-playing'`, so the page still owns
 * the full viewport visually even though it's mounted inside the shell.
 *
 * Menu wiring (Batch 6.2.4 Commit 3):
 *   - NowPlayingMenu fires `onShareClick` → async `fetchStoryById` →
 *     open ShareBottomSheet with full story detail. Sheet renders
 *     immediately with `loading=true` until the fetch resolves so the
 *     user sees the title + spinner instead of a delay. Fetch error →
 *     close sheet + show error Toast.
 *   - NowPlayingMenu fires `onRateClick` → navigate to
 *     `/story/:storyId?scrollTo=rating`. StoryRoute reads the query
 *     param on mount and scrolls the Rating-Widget into view.
 *
 * Schlaf-Timer wiring (Batch 6.2.3 Commit 4):
 *   - The Schlaf-Timer button inside AudioMiniControls opens
 *     <SchlafTimerSheet /> via `sheetOpen` state owned here.
 */
export function NowPlayingPage() {
  const navigate = useNavigate();
  const title = useAudioStore((s) => s.title);
  const childName = useAudioStore((s) => s.childName);
  const worldSlug = useAudioStore((s) => s.worldSlug);
  const storyId = useAudioStore((s) => s.storyId);

  const worldName = worldSlug ? WORLD_NAME_BY_SLUG[worldSlug] : '';

  const [sheetOpen, setSheetOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareStory, setShareStory] = useState<StoryDetail | null>(null);
  const [shareErrorToastVisible, setShareErrorToastVisible] = useState(false);

  const handleShareClick = async () => {
    if (!storyId) return;
    // Open sheet immediately in loading state so the user has instant
    // feedback while the story detail fetch resolves.
    setShareStory(null);
    setShareOpen(true);
    try {
      const story = await fetchStoryById(storyId);
      setShareStory(story);
    } catch (err) {
      console.error(err);
      setShareOpen(false);
      if (err instanceof ApiError || err instanceof NetworkError) {
        setShareErrorToastVisible(true);
        window.setTimeout(() => setShareErrorToastVisible(false), ERROR_TOAST_DURATION_MS);
      }
    }
  };

  const handleRateClick = () => {
    if (!storyId) return;
    navigate(`/story/${storyId}?scrollTo=rating`);
  };

  const shareMode = shareStory ? resolveStoryMode(shareStory.storyType) : 'standard';

  return (
    <div className="flex min-h-screen flex-col bg-gradient-to-t from-dream-navy to-world-aa text-dream-white">
      <NowPlayingTopNav
        menuSlot={
          // Rating ("Bewerten") + share are story-only affordances (they route by
          // storyId). A catalog track has storyId=null → omit the menu so they
          // don't render as dead buttons; TopNav falls back to an inert 3-dots
          // placeholder. The story path (storyId present) is byte-identical.
          storyId ? (
            <NowPlayingMenu
              onShareClick={() => {
                void handleShareClick();
              }}
              onRateClick={handleRateClick}
            />
          ) : undefined
        }
      />

      <div className="flex flex-1 flex-col items-center justify-center gap-6 px-6 pb-8 md:px-10 md:pb-12 desktop:flex-row desktop:items-center desktop:justify-center desktop:gap-16 desktop:px-20 desktop:pb-16">
        <NowPlayingCover />

        <div className="flex w-full flex-col items-center gap-4 md:max-w-[468px] desktop:w-[360px] desktop:max-w-none">
          <AudioTitleMeta
            title={title ?? ''}
            childName={childName}
            worldName={worldName}
            createdDate=""
            titleTestId="nowplaying-title"
          />
          <AudioProgressBar className="desktop:max-w-[360px]" />
          <AudioBigControls />
          <AudioMiniControls onSchlafTimerClick={() => setSheetOpen(true)} />
        </div>

        <SchlafTimerSheet open={sheetOpen} onOpenChange={setSheetOpen} />
      </div>

      <ShareBottomSheet
        open={shareOpen}
        onOpenChange={setShareOpen}
        loading={shareStory === null}
        childName={shareStory?.childName ?? null}
        storyTitle={shareStory?.title ?? title ?? ''}
        mode={shareMode}
      />

      <Toast
        message={STRINGS.storyOutput.page.share.sheet.errors.loadFailed}
        visible={shareErrorToastVisible}
      />
    </div>
  );
}
